import React, { useEffect, useState, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import SideBar from "../other/SideBar";
import NavBar from "../other/NavBar";
import LoadingModal from "../../loader/LoadingModal";
import {
  AddAssignTablesForStaffURL,
  GetAssignTablesDataForStaffURL,
} from "../../assets/api/BaseURL";
import Notification from "../other/Notification";
import { useToast } from "../../../context/ToastContext";

const AssignTablesToStaff = () => {
  const navigate = useNavigate();
  const { addToast } = useToast();
  const [token, setToken] = useState("");
  const [toggleSideBar, setToggleSideBar] = useState(false);
  const [canvas, setCanvas] = useState(false);
  const [staffID, setStaffID] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [flag, setFlag] = useState(false);
  const [tablesData, setTablesData] = useState([]);
  const [categoriesData, setCategoriesData] = useState([]);
  const [assignTable, setAssignTable] = useState([]);
  const [disabledTables, setDisabledTables] = useState([]);

  const handleToggleSidebar = (e) => {
    e.preventDefault();
    setToggleSideBar(!toggleSideBar);
  };

  const handleToggleOffCanvas = (e) => {
    e.preventDefault();
    setCanvas(!canvas);
  };

  const handleBackClick = (e) => {
    e.preventDefault();
    navigate("/view_staff");
  };

  const isTableDisabled = (tableId) => {
    return disabledTables.some(disabledTable => disabledTable.table_id === tableId);
  };

  const handleCheckboxChange = (e, id) => {
    if (isTableDisabled(id)) return;

    setTablesData(prevData =>
      prevData.map(item =>
        item.id === id ? { ...item, checked: e.target.checked } : item
      )
    );
  };

  // Group tables by category
  const groupedTables = useMemo(() => {
    const groups = {};

    // First, create categories from categoriesData
    categoriesData.forEach(category => {
      groups[category.category_id] = {
        category_id: category.category_id,
        table_categoryname: category.table_categoryname,
        tables: []
      };
    });

    // Add "Other" category for tables with category_id not in categoriesData or 0
    groups["other"] = {
      category_id: "other",
      table_categoryname: "Other",
      tables: []
    };

    // Assign tables to their respective categories
    tablesData.forEach(table => {
      if (table.category_id === 0 || !groups[table.category_id]) {
        groups["other"].tables.push(table);
      } else {
        if (groups[table.category_id]) {
          groups[table.category_id].tables.push(table);
        }
      }
    });

    // Filter out categories that have no tables
    return Object.values(groups).filter(group => group.tables.length > 0);
  }, [tablesData, categoriesData]);

  const handleSubmitClick = async (e) => {
    e.preventDefault();

    // Filter out disabled tables from checked items
    const checkedIds = tablesData
      .filter((item) => item.checked && !isTableDisabled(item.id))
      .map((item) => item.id);

    const dataToSend = {
      staff_id: staffID,
      table_ids: checkedIds, // Can be empty array
    };

    setIsLoading(true);
    try {
      const response = await axios({
        method: "post",
        url: AddAssignTablesForStaffURL,
        data: dataToSend,
        headers: {
          "Content-Type": "application/json",
          authorization: `Bearer ${token}`,
        },
      });

      if (response.status === 200 && response.data.message) {
        setFlag(!flag);
        setError("");
        setSuccess(response.data.message);
        addToast("success", response.data.message);
      }
    } catch (error) {
      if (error.response && error.response.data.message) {
        setError(error.response.data.message);
        setSuccess("");
        addToast("error", error.response.data.message);
      } else {
        setError(error.message);
        setSuccess("");
        addToast("error", error.message);
      }
      console.log(error);
    }
    setIsLoading(false);
  };

  useEffect(() => {
    if (!localStorage.getItem("token")) {
      navigate("/auth");
    }
  }, [navigate]);

  useEffect(() => {
    setToken(localStorage.getItem("token"));
  }, [token]);

  useEffect(() => {
    setStaffID(localStorage.getItem("staffID"));
  }, [staffID]);

  useEffect(() => {
    if (token && staffID) {
      async function getData() {
        setIsLoading(true);
        try {
          const response = await axios.get(GetAssignTablesDataForStaffURL, {
            params: {
              staff_id: staffID,
            },
            headers: { authorization: `Bearer ${token}` },
          });

          if (response.status === 200 && response.data.data) {
            // Update tables data with checked status
            const updatedTables = response.data.data.map((item) => ({
              ...item,
              checked: response.data.assignTable.some(
                (assignItem) => assignItem.table_id === item.id
              ),
            }));

            setTablesData(updatedTables);

            // Set categories data
            if (response.data.categoryData) {
              setCategoriesData(response.data.categoryData);
            }

            // Set assignTable data
            if (response.data.assignTable) {
              setAssignTable(response.data.assignTable);
            }

            // Set disabled tables data
            if (response.data.disibleTable) {
              setDisabledTables(response.data.disibleTable);
            }
          }
        } catch (error) {
          if (error.response && error.response.data.message) {
            addToast("error", error.response.data.message);
          } else {
            addToast("error", error.message);
          }
          console.log(error);
        }
        setIsLoading(false);
      }
      getData();
    }
  }, [token, staffID, flag, addToast]);

  useEffect(() => {
    if (error || success) {
      window.scrollTo(0, 0);
    }
  }, [error, success]);

  return (
    <>
      {token && (
        <>
          <div
            className={
              toggleSideBar
                ? "container-scroller sidebar-icon-only"
                : "container-scroller"
            }
          >
            <nav className="navbar default-layout col-lg-12 col-12 p-0 fixed-top d-flex align-items-top flex-row">
              <div className="text-center navbar-brand-wrapper d-flex align-items-center justify-content-start">
                <Link className="navbar-brand brand-logo-mini" to="/fooders_cp">
                  {/* <img src="images/mini-kr-logo.svg" alt="logo" />     */}
                </Link>
                <div className="me-1">
                  <button
                    className={
                      toggleSideBar
                        ? "navbar-toggler navbar-toggler align-self-center"
                        : "navbar-toggler navbar-toggler align-self-center text-white"
                    }
                    type="button"
                    onClick={handleToggleSidebar}
                  >
                    <span className="icon-menu"></span>
                  </button>
                </div>
                <div>
                  <Link to="/fooders_cp">
                    <span className="navbar-brand brand-logo">
                      <img src="images/vyfoo_logo.png" alt="logo" />
                    </span>
                  </Link>
                </div>
              </div>
              <NavBar handleToggle={handleToggleOffCanvas} toggleCanvas={toggleSideBar} />
            </nav>
            <div className="container-fluid page-body-wrapper px-0">
              <SideBar active="view_staff" toggleCanvas={toggleSideBar} />

              <div className="main-panel">
                <div className="content-wrapper">
                  <div className="row">
                    <div className="col-sm-12">
                      <div className="row flex-grow">
                        <div className="col-12 grid-margin stretch-card">
                          <div className="card card-rounded">
                            <div className="card-body">
                              <div className="d-flex justify-content-between align-items-start mb-2">
                                <div className="vsalign">
                                  <h4 className="card-title card-title-dash">
                                    Assign Tables
                                  </h4>
                                  <p>The table that is already disabled is assigned to another staff member.</p>
                                  <h6 className="text-danger">Table assignment feature is available only on the offline POS system.</h6>

                                </div>
                                <div className="vsalign">
                                  <span
                                    className="btn btn-danger mb-0 me-0"
                                    onClick={handleBackClick}
                                  >
                                    <i className="menu-icon mdi mdi-arrow-left vsalign"></i>
                                    Go Back
                                  </span>
                                </div>
                              </div>
                              <hr />
                              <form className="forms-sample">
                                {error && (
                                  <div className="d-flex justify-content-between align-items-start alert alert-danger">
                                    <div className="vsalign">{error}</div>
                                    <div className="vsalign">
                                      <span
                                        className=".h3 text-muted"
                                        style={{ cursor: "pointer" }}
                                        onClick={() => setError("")}
                                      >
                                        x
                                      </span>
                                    </div>
                                  </div>
                                )}
                                {success && (
                                  <div className="d-flex justify-content-between align-items-start alert alert-success">
                                    <div className="vsalign">{success}</div>
                                    <div className="vsalign">
                                      <span
                                        className=".h3 text-muted"
                                        style={{ cursor: "pointer" }}
                                        onClick={() => setSuccess("")}
                                      >
                                        x
                                      </span>
                                    </div>
                                  </div>
                                )}

                                {/* Grouped Tables by Category */}
                                {groupedTables.length === 0 ? (
                                  <div className="text-center py-5">
                                    <p className="text-muted">No tables available for assignment.</p>
                                  </div>
                                ) : (
                                  groupedTables.map((categoryGroup) => (
                                    <div key={categoryGroup.category_id} className="mb-4">
                                      <h5 className="mb-3">
                                        {categoryGroup.table_categoryname}
                                      </h5>
                                      <div className="row">
                                        {categoryGroup.tables.map((item) => {
                                          const isDisabled = isTableDisabled(item.id);
                                          return (
                                            <div className="col-12 col-md-6 col-lg-4 mb-2" key={item.id}>
                                              <div className="form-check">
                                                <input
                                                  className="form-check-input"
                                                  type="checkbox"
                                                  id={`table-${item.id}`}
                                                  checked={item.checked || false}
                                                  onChange={(e) => handleCheckboxChange(e, item.id)}
                                                  disabled={isDisabled}
                                                />
                                                <label
                                                  className={`form-check-label ms-2 d-flex align-items-center gap-2 ${isDisabled ? 'text-muted' : ''
                                                    }`}
                                                  htmlFor={`table-${item.id}`}
                                                >
                                                  <span>
                                                    {(item.table_name ||
                                                      (item.type === 1 ? "Counter" : "Table"))} - {item.table_no}
                                                  </span>

                                                  {item.type === 1 && (
                                                    <span className="badge bg-info">Counter</span>
                                                  )}

                                                  {item.type === 2 && (
                                                    <span className="badge bg-primary">Foreign</span>
                                                  )}
                                                </label>
                                              </div>
                                            </div>
                                          );
                                        })}
                                      </div>
                                    </div>
                                  ))
                                )}

                                <button
                                  type="submit"
                                  className="btn btn-primary me-2 mt-4"
                                  onClick={handleSubmitClick}
                                >
                                  Submit
                                </button>
                              </form>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <LoadingModal isLoading={isLoading} />

          <Notification />
        </>
      )}
    </>
  );
};

export default AssignTablesToStaff;